import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Table, Spinner } from 'react-bootstrap';

export function QuestionImportModal({ show, onHide, course, onImportSuccess }) {
  const [csvFile, setCsvFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [rowValidationResults, setRowValidationResults] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [newSections, setNewSections] = useState(new Set());

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      setParsedData([]);
      setRowValidationResults([]);
      setErrors([]);
      setShowPreview(false);
    }
  };

  // Helper function to parse CSV line respecting quoted fields
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  };

  const parseCSV = (csvText) => {
    setErrors([]);
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      setErrors(['CSV must have at least a header row and one data row']);
      return false;
    }

    // Parse header
    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());
    const requiredHeaders = ['section', 'type', 'question'];
    const hasRequired = requiredHeaders.every(h => headers.includes(h));

    if (!hasRequired) {
      setErrors([`CSV must contain these columns: ${requiredHeaders.join(', ')}`]);
      return false;
    }

    // Parse rows
    const parsed = [];
    const validationResults = [];
    const foundNewSections = new Set();

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines

      const values = parseCSVLine(line);

      // Skip rows where all values are empty (separator rows)
      if (values.every(v => !v)) continue;
      
      const row = {};

      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });

      const validation = validateRow(row, i + 1, course, foundNewSections);
      validationResults.push({
        lineNum: i + 1,
        data: validation.data,
        errors: validation.errors,
        isValid: validation.isValid,
      });

      if (validation.isValid) {
        parsed.push(validation.data);
        if (validation.isNewSection) {
          foundNewSections.add(validation.data.section);
        }
      }
    }

    if (validationResults.length === 0) {
      setErrors(['No question rows found in CSV']);
      return false;
    }

    setParsedData(parsed);
    setRowValidationResults(validationResults);
    setNewSections(foundNewSections);
    setShowPreview(true);
    return true;
  };

  const validateRow = (row, lineNum, course, newSectionsSet) => {
    const errors = [];
    const data = {
      section: row.section || '',
      type: row.type || '',
      question: row.question || '',
      correctAnswer: row.correct_answer || row.correctanswer || '',
      options: row.options || '',
      explanation: row.explanation || '',
      tags: row.tags || '',
      difficulty: row.difficulty || 'medium',
      leftItems: row.left_items || '',
      rightItems: row.right_items || '',
      items: row.items || '',
    };
    const type = data.type.toLowerCase();
    const normalizeValue = (value) => String(value || '').trim().toLowerCase();

    // Validate required fields
    if (!data.section.trim()) {
      errors.push(`Row ${lineNum}: Section is required`);
    }

    if (!data.type.trim()) {
      errors.push(`Row ${lineNum}: Type is required`);
    }

    if (!data.question.trim()) {
      errors.push(`Row ${lineNum}: Question text is required`);
    }

    // Validate question type
    const validTypes = ['multiple-choice', 'true-false', 'fill-in', 'multi-select', 'matching', 'drag-drop'];
    if (data.type && !validTypes.includes(type)) {
      errors.push(`Row ${lineNum}: Invalid question type "${data.type}". Must be one of: ${validTypes.join(', ')}`);
    }

    // Validate type-specific requirements
    if (['multiple-choice', 'multi-select'].includes(type)) {
      const options = data.options
        .split(';')
        .map((opt) => opt.trim())
        .filter(Boolean);
      const normalizedOptions = new Set(options.map(normalizeValue));

      if (!data.options.trim()) {
        errors.push(`Row ${lineNum}: ${data.type} requires options (semicolon-separated)`);
      } else if (options.length < 2) {
        errors.push(`Row ${lineNum}: ${data.type} requires at least 2 options`);
      }

      if (!data.correctAnswer.trim()) {
        errors.push(`Row ${lineNum}: Correct answer is required for ${data.type}`);
      } else if (type === 'multiple-choice') {
        if (data.correctAnswer.includes(';')) {
          errors.push(`Row ${lineNum}: Multiple-choice must have only one correct answer`);
        }
        if (normalizedOptions.size > 0 && !normalizedOptions.has(normalizeValue(data.correctAnswer))) {
          errors.push(`Row ${lineNum}: Correct answer is not found in options`);
        }
      } else {
        const selectedAnswers = data.correctAnswer
          .split(';')
          .map((answer) => answer.trim())
          .filter(Boolean);
        const unmatchedAnswers = selectedAnswers.filter(
          (answer) => !normalizedOptions.has(normalizeValue(answer))
        );

        if (selectedAnswers.length === 0) {
          errors.push(`Row ${lineNum}: Multi-select requires at least one correct answer`);
        }
        if (unmatchedAnswers.length > 0) {
          errors.push(`Row ${lineNum}: Multi-select correct answer(s) not found in options: ${unmatchedAnswers.join(', ')}`);
        }
      }
    }

    if (type === 'true-false') {
      if (!data.correctAnswer.trim()) {
        errors.push(`Row ${lineNum}: Correct answer is required for true-false`);
      } else if (!['true', 'false'].includes(normalizeValue(data.correctAnswer))) {
        errors.push(`Row ${lineNum}: True-false correct_answer must be True or False`);
      }
    }

    if (type === 'fill-in' && !data.correctAnswer.trim()) {
      errors.push(`Row ${lineNum}: Fill-in question requires correct_answer`);
    }

    if (type === 'matching') {
      const leftItems = data.leftItems
        .split(';')
        .map((item) => item.trim())
        .filter(Boolean);
      const rightItems = data.rightItems
        .split(';')
        .map((item) => item.trim())
        .filter(Boolean);

      if (!data.leftItems.trim()) {
        errors.push(`Row ${lineNum}: Matching question requires left_items`);
      }
      if (!data.rightItems.trim()) {
        errors.push(`Row ${lineNum}: Matching question requires right_items`);
      }
      if (leftItems.length > 0 && rightItems.length > 0 && leftItems.length !== rightItems.length) {
        errors.push(`Row ${lineNum}: Matching question requires the same number of left_items and right_items`);
      }
    }

    if (type === 'drag-drop') {
      const items = data.items
        .split(';')
        .map((item) => item.trim())
        .filter(Boolean);

      if (!data.items.trim()) {
        errors.push(`Row ${lineNum}: Drag-drop question requires items`);
      } else if (items.length < 2) {
        errors.push(`Row ${lineNum}: Drag-drop question requires at least 2 items`);
      }
      if (!data.correctAnswer.trim()) {
        errors.push(`Row ${lineNum}: Drag-drop question requires correct_answer order`);
      }
    }

    // Check if section exists
    const sectionExists = course.sections?.some(s => s.title.toLowerCase() === data.section.toLowerCase());
    if (!sectionExists && !newSectionsSet.has(data.section)) {
      newSectionsSet.add(data.section);
    }

    return {
      isValid: errors.length === 0,
      errors,
      data,
      isNewSection: !sectionExists,
    };
  };

  const handleParseClick = () => {
    if (!csvFile) {
      setErrors(['Please select a CSV file']);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        parseCSV(e.target.result);
      } catch (err) {
        setErrors([`Error reading file: ${err.message}`]);
      }
    };
    reader.readAsText(csvFile);
  };

  const handleImportClick = async () => {
    setIsProcessing(true);
    try {
      // Import logic will be handled by the parent component
      await onImportSuccess(parsedData);
      handleClose();
    } catch (err) {
      setErrors([`Import failed: ${err.message}`]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setCsvFile(null);
    setParsedData([]);
    setRowValidationResults([]);
    setErrors([]);
    setShowPreview(false);
    setNewSections(new Set());
    onHide();
  };

  const invalidRows = rowValidationResults.filter((row) => !row.isValid);
  const validRowCount = parsedData.length;
  const invalidRowCount = invalidRows.length;

  const handleDownloadSample = () => {
    // Create a link to download the sample CSV
    const link = document.createElement('a');
    link.href = '/assets/sample-questions-import.csv';
    link.download = 'sample-questions-import.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>📥 Import Questions from CSV</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* CSV Format Info */}
        <Alert variant="info" className="mb-3">
          <strong>CSV Format:</strong> The CSV must include these columns:
          <ul className="mb-0 mt-2">
            <li><code>section</code> - Module/section name (creates new if not found)</li>
            <li><code>type</code> - Question type: multiple-choice, true-false, fill-in, multi-select, matching, drag-drop</li>
            <li><code>question</code> - The question text</li>
            <li><code>correct_answer</code> - Correct answer (for multi-select, use semicolon-separated list)</li>
            <li><code>options</code> - Answer options for multiple choice (semicolon-separated)</li>
            <li><code>explanation</code> (optional) - Explanation for the answer</li>
            <li><code>tags</code> (optional) - Tags for the question (semicolon-separated)</li>
            <li><code>difficulty</code> (optional) - easy, medium, or hard</li>
          </ul>
          <div className="mt-2 pt-2 border-top">
            <small>For matching: use columns <code>left_items</code> and <code>right_items</code> (semicolon-separated)</small><br/>
            <small>For drag-drop: use column <code>items</code> (semicolon-separated) and <code>correct_answer</code> with correct order</small>
          </div>
          <div className="mt-3 pt-3 border-top">
            <Button 
              variant="outline-info" 
              size="sm"
              onClick={handleDownloadSample}
            >
              📥 Download Sample CSV
            </Button>
          </div>
        </Alert>

        {/* File Input */}
        {!showPreview && (
          <Form.Group className="mb-3">
            <Form.Label>Select CSV File</Form.Label>
            <Form.Control
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isProcessing}
            />
          </Form.Group>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <Alert variant="danger" className="mb-3">
            <strong>Errors found:</strong>
            <ul className="mb-0 mt-2">
              {errors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Preview Table */}
        {showPreview && rowValidationResults.length > 0 && (
          <div>
            <Alert variant={invalidRowCount > 0 ? 'warning' : 'success'} className="mb-3">
              Parsed <strong>{rowValidationResults.length}</strong> row(s):{' '}
              <strong>{validRowCount}</strong> valid, <strong>{invalidRowCount}</strong> invalid.
              {invalidRowCount > 0 && (
                <div className="mt-2">
                  Rows highlighted in red are invalid and will be skipped during import.
                  <div className="small mt-1">
                    Invalid row numbers: {invalidRows.map((row) => row.lineNum).join(', ')}
                  </div>
                </div>
              )}
              {newSections.size > 0 && (
                <div className="mt-2">
                  New sections to create: <strong>{Array.from(newSections).join(', ')}</strong>
                </div>
              )}
            </Alert>

            <div className="mb-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Section</th>
                    <th>Type</th>
                    <th>Question</th>
                    <th>Answer</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rowValidationResults.map((row) => {
                    const questionPreview = row.data.question || '';
                    const answerPreview = row.data.correctAnswer || '';
                    return (
                    <tr key={`row-${row.lineNum}`} className={row.isValid ? '' : 'table-danger'}>
                      <td>{row.lineNum}</td>
                      <td>{row.data.section}</td>
                      <td><code>{row.data.type}</code></td>
                      <td>{questionPreview ? `${questionPreview.substring(0, 40)}...` : '-'}</td>
                      <td><code>{String(answerPreview).substring(0, 30) || '-'}</code></td>
                      <td>
                        {row.isValid ? (
                          <span className="text-success fw-semibold">Valid</span>
                        ) : (
                          <>
                            <span className="text-danger fw-semibold">Invalid</span>
                            {row.errors.length > 0 && (
                              <div className="small text-danger mt-1">{row.errors[0]}</div>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  )})}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!showPreview ? (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleParseClick}
              disabled={!csvFile || isProcessing}
            >
              {isProcessing && <Spinner as="span" animation="border" size="sm" className="me-2" />}
              Parse CSV
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setShowPreview(false)}>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleImportClick}
              disabled={isProcessing || validRowCount === 0}
            >
              {isProcessing && <Spinner as="span" animation="border" size="sm" className="me-2" />}
              {isProcessing
                ? 'Importing...'
                : `Import ${validRowCount} Valid Question${validRowCount === 1 ? '' : 's'}`}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
